import { Table, Button, Space, Tooltip, Divider } from "antd";
import { CfdiComplementModel } from "api/generated";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useCallback, useMemo } from "react";
import type { TablePaginationConfig, TableProps } from "antd";
import dayjs from "dayjs";
import { ColumnType } from "antd/es/table";
import { useIsMobile } from "hooks/useIsMobile";
import {
  UsePaginationTableReturn,
  useSelfPagination,
} from "hooks/commons/usePaginationTable";
import { useColumnSelector } from "hooks/commons/useColumnSelector";
import { ColumnSelector } from "common/components/table/ColumnSelector";
import { getColumnOrder } from "common/helpers/tableUtils";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { ColumnFilterHeader } from "common/components/table/ColumnFilterHeader";

export interface CFDITableRecord extends Partial<CfdiComplementModel> {
  key: number;
}

interface CfdiTableProps {
  data: Partial<CfdiComplementModel>[];
  onEdit?: (index: number) => void;
  onRemove?: (index: number) => void;
  pagination?: UsePaginationTableReturn<CfdiComplementModel>;
  hideActions?: boolean;
  handleTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CFDITableRecord> | SorterResult<CFDITableRecord>[],
  ) => void;
  handleRfcFilters?: (
    rfcType: "rfcReceptor" | "rfcEmisor",
    rfcValue: string,
  ) => void;
  rfcReceptorFilter?: string;
  rfcEmisorFilter?: string;
}

// Columnas disponibles
const availableColumns = [
  { key: "fiscalId", title: "UUID SAT", order: 0, alwaysVisible: true },
  { key: "cfdiType", title: "Tipo de CFDI", order: 1, defaultVisible: true },
  {
    key: "invoiceDate",
    title: "Fecha Factura",
    order: 2,
    defaultVisible: true,
  },
  { key: "status", title: "Estatus", order: 3, defaultVisible: true },
  { key: "rfcReceptor", title: "RFC Receptor", order: 4 },
  { key: "nameReceptor", title: "Nombre Receptor", order: 5 },
  { key: "rfcEmisor", title: "RFC Emisor", order: 6 },
  { key: "nameEmisor", title: "Nombre Emisor", order: 7 },
  { key: "price", title: "Precio", order: 8 },
  { key: "volume", title: "Volumen", order: 9 },
  { key: "amount", title: "Monto", order: 10 },
  { key: "serie", title: "Serie", order: 11 },
  { key: "folio", title: "Folio", order: 12 },
  { key: "description", title: "Descripción", order: 13 },
  { key: "permission", title: "Permiso", order: 14 },
  { key: "actions", title: "Acciones", order: 15, alwaysVisible: true },
];

export function CfdiTable({
  data,
  onEdit,
  onRemove,
  pagination,
  hideActions = false,
  handleTableChange,
  handleRfcFilters,
  rfcReceptorFilter,
  rfcEmisorFilter,
}: CfdiTableProps) {
  const { isMobile } = useIsMobile();
  const dataOrigin: CfdiComplementModel[] = useMemo(
    () => (pagination ? pagination.items : data),
    [pagination?.items, data],
  );

  const dataSource = useMemo(
    () =>
      dataOrigin.map((item, index) => ({
        ...item,
        key: index,
      })),
    [dataOrigin],
  );
  const selfPagination = useSelfPagination(dataSource);

  // Obtener el orden de columnas basado en la configuración
  const columnOrder = useMemo(() => getColumnOrder(availableColumns), []);

  const {
    visibleColumns,
    toggleColumn,
    toggleAllColumns,
    allSelected,
    indeterminate,
    dropdownButtonText,
  } = useColumnSelector({
    tableId: "cfdi-table",
    availableColumns,
  });

  // Handler para filtros de RFC
  const handleRfcFilterChange = useCallback(
    (dataIndex: string, value: string) => {
      if (
        handleRfcFilters &&
        (dataIndex === "rfcReceptor" || dataIndex === "rfcEmisor")
      ) {
        handleRfcFilters(dataIndex as "rfcReceptor" | "rfcEmisor", value);
      }
    },
    [handleRfcFilters],
  );

  // Generar columnas de la tabla
  const tableColumns = useMemo((): TableProps<CFDITableRecord>["columns"] => {
    const columnMap: Record<string, ColumnType<CFDITableRecord>> = {
      fiscalId: {
        title: "UUID SAT",
        dataIndex: "fiscalId",
        key: "fiscalId",
        fixed: isMobile ? undefined : "left",
        width: "12rem",
        sorter: handleTableChange ? true : undefined,
      },
      cfdiType: {
        title: "Tipo de CFDI",
        dataIndex: "cfdiType",
        key: "cfdiType",
        width: "8rem",
        filters: handleTableChange
          ? [
              { text: "Ingreso", value: "INGRESO" },
              { text: "Egreso", value: "EGRESO" },
              { text: "Traspaso", value: "TRASLADO" },
            ]
          : undefined,
      },
      invoiceDate: {
        title: "Fecha Factura",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        width: "10rem",
        sorter: handleTableChange ? true : undefined,
        render: (value) =>
          value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-",
      },
      status: {
        title: "Estatus",
        dataIndex: "status",
        key: "status",
        width: "8rem",
      },

      rfcReceptor: {
        title: (
          <ColumnFilterHeader
            title="RFC Receptor"
            dataIndex="rfcReceptor"
            activeFilter={rfcReceptorFilter}
            onFilterChange={handleRfcFilterChange}
            allowSearch={handleTableChange ? true : false}
          />
        ),
        dataIndex: "rfcReceptor",
        key: "rfcReceptor",
        width: "10rem",
        sorter: handleTableChange ? true : undefined,
      },
      nameReceptor: {
        title: "Nombre Receptor",
        dataIndex: "nameReceptor",
        key: "nameReceptor",
        ellipsis: true,
        width: "10rem",
        sorter: handleTableChange ? true : undefined,
      },
      rfcEmisor: {
        title: (
          <ColumnFilterHeader
            title="RFC Emisor"
            dataIndex="rfcEmisor"
            activeFilter={rfcEmisorFilter}
            onFilterChange={handleRfcFilterChange}
            allowSearch={handleTableChange ? true : false}
          />
        ),
        dataIndex: "rfcEmisor",
        key: "rfcEmisor",
        width: "12rem",
        sorter: handleTableChange ? true : undefined,
      },

      nameEmisor: {
        title: "Nombre Emisor",
        dataIndex: "nameEmisor",
        key: "nameEmisor",
        ellipsis: true,
        width: "10rem",
        sorter: handleTableChange ? true : undefined,
      },
      price: {
        title: "Precio",
        dataIndex: "price",
        key: "price",
        width: "8rem",
        render: (value?: number) => value?.toFixed(3) || "-",
      },
      volume: {
        title: "Volumen",
        dataIndex: "volume",
        key: "volume",
        width: "8rem",
        render: (value?: number) => value?.toFixed(3) || "-",
      },
      amount: {
        title: "Monto",
        dataIndex: "amount",
        key: "amount",
        width: "8rem",
        render: (value?: number) => value?.toFixed(3) || "-",
      },
      serie: {
        title: "Serie",
        dataIndex: "invoiceSerie",
        key: "invoiceSerie",
        width: "8rem",
        sorter: handleTableChange ? true : undefined,
      },
      folio: {
        title: "Folio",
        dataIndex: "invoiceFolio",
        key: "invoiceFolio",
        width: "8rem",
        sorter: handleTableChange ? true : undefined,
      },
      description: {
        title: "Descripción",
        dataIndex: "description",
        key: "description",
        width: "12rem",
        sorter: handleTableChange ? true : undefined,
      },
      permission: {
        title: "Permiso",
        dataIndex: "permission",
        key: "permission",
        width: "12rem",
        sorter: handleTableChange ? true : undefined,
      },
      actions: {
        title: "Acciones",
        key: "actions",
        width: "6rem",
        fixed: "right",
        render: (value: unknown, record: CFDITableRecord, index: number) => (
          <Space>
            {onEdit && (
              <Tooltip title="Editar">
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(index)}
                />
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip title="Remover">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onRemove(index)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    };

    // Ordenar las columnas según columnOrder y filtrar solo las visibles
    const orderedColumns = columnOrder
      .filter((colKey) => visibleColumns.includes(colKey))
      .map((colKey) => columnMap[colKey])
      .filter((col) => (hideActions ? col.title !== "Acciones" : true))
      .filter(Boolean) as ColumnType<CFDITableRecord>[];

    return orderedColumns;
  }, [visibleColumns, isMobile, onEdit, onRemove, columnOrder]);

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ColumnSelector
          tableId="cfdi-table"
          availableColumns={availableColumns}
          visibleColumns={visibleColumns}
          toggleColumn={toggleColumn}
          toggleAllColumns={toggleAllColumns}
          allSelected={allSelected}
          indeterminate={indeterminate}
          dropdownButtonText={dropdownButtonText}
        />
      </div>

      {/* Tabla de CFDIs */}
      <Table
        dataSource={dataSource}
        columns={tableColumns}
        pagination={{
          ...(pagination?.paginationConfig ?? selfPagination),
          onChange: pagination?.onPageChange,
          onShowSizeChange: pagination?.onPageSizeChange,
        }}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        locale={{
          emptyText: pagination
            ? "No existen registros de CFDIs bajo los filtros seleccionados"
            : "No hay CFDI registrados. Usa el formulario inferior para agregar uno.",
        }}
      />
      <Divider />
    </div>
  );
}
